import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Typography,
  type TableProps,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, ResponsiveList } from '@mall/shared';
import { useToast } from '../../app/ToastProvider';
import { createBrand, deleteBrand, listBrands, updateBrand } from './api';
import type { BrandUpsertRequest, BrandView } from './types';

interface FormValues {
  name: string;
  sortOrder: number;
}

const EMPTY: FormValues = { name: '', sortOrder: 0 };

export function BrandsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({ queryKey: ['admin', 'brands'], queryFn: listBrands });
  const [form] = Form.useForm<FormValues>();
  const [editing, setEditing] = useState<BrandView | null>(null);
  const [open, setOpen] = useState(false);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(EMPTY);
    setOpen(true);
  };

  const openEdit = (record: BrandView) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      sortOrder: record.sortOrder ?? 0,
    });
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'brands'] });

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: BrandUpsertRequest = {
        name: values.name.trim(),
        sortOrder: values.sortOrder ?? 0,
      };
      if (editing) {
        return updateBrand(editing.id, payload);
      }
      return createBrand(payload);
    },
    onSuccess: () => {
      toast.success(editing ? '已更新' : '已创建');
      setOpen(false);
      void invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '保存失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteBrand(id),
    onSuccess: () => {
      toast.success('已删除');
      void invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '删除失败');
    },
  });

  const columns: TableProps<BrandView>['columns'] = [
    { title: 'ID', dataIndex: 'id', width: 80, fixed: 'left' },
    {
      title: '名称',
      dataIndex: 'name',
      render: (name: string) => <span className="mall-row-title">{name}</span>,
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 100,
      sorter: (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      render: (value: number | null | undefined) => value ?? 0,
    },
    {
      title: '操作',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title={`确认删除品牌「${record.name}」？`}
            okText="删除"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Brands</div>
          <Typography.Title level={3} className="mall-page-title">
            品牌管理
          </Typography.Title>
          <p className="mall-page-description">维护当前商户可用品牌，并控制品牌在前台的排序。</p>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => query.refetch()}
            loading={query.isFetching}
          >
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建品牌
          </Button>
        </Space>
      </div>
      <div className="mall-table-card">
        <div className="mall-table-toolbar">
          <div>
            <div className="mall-table-title">品牌列表</div>
            <div className="mall-table-subtitle">共 {query.data?.length ?? 0} 个品牌</div>
          </div>
        </div>
        <ResponsiveList<BrandView>
          rowKey={(record) => record.id}
          loading={query.isLoading}
          dataSource={query.data ?? []}
          emptyText="暂无品牌"
          pagination={{
            pageSize: 20,
            showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`,
          }}
          tableProps={{
            size: 'small',
            columns,
            scroll: { x: 720 },
            locale: { emptyText: '暂无品牌' },
          }}
          renderCard={(record) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div className="mall-row-title">{record.name}</div>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                  #{record.id} · 排序 {record.sortOrder ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
                  编辑
                </Button>
                <Popconfirm
                  title={`确认删除品牌「${record.name}」？`}
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => deleteMutation.mutate(record.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              </div>
            </div>
          )}
        />
      </div>
      {query.isError && (
        <Typography.Paragraph type="danger" style={{ marginTop: 16 }}>
          列表加载失败：{query.error instanceof Error ? query.error.message : '未知错误'}
        </Typography.Paragraph>
      )}

      <Modal
        title={editing ? `编辑品牌 #${editing.id}` : '新建品牌'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isPending}
        destroyOnClose
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
          initialValues={EMPTY}
        >
          <Form.Item label="名称" name="name" rules={[{ required: true, message: '名称必填' }]}>
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item
            label="排序"
            name="sortOrder"
            tooltip="数字越小越靠前；列表与商城前台均按升序展示"
          >
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
